import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders, HttpResponse, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Component, Directive, ElementRef, EventEmitter, forwardRef, HostListener, Injectable, InjectionToken, Input, ModuleWithProviders, NgModule, Output } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ToastrService {
  success(message?: string, title?: string): void { this.show('success', message, title); }
  error(message?: string, title?: string): void { this.show('error', message, title); }
  warning(message?: string, title?: string): void { this.show('warning', message, title); }
  info(message?: string, title?: string): void { this.show('info', message, title); }

  private show(type: string, message?: string, title?: string): void {
    const text = [title, message].filter(Boolean).join(' - ');
    if (text) {
      console[type === 'error' ? 'error' : type === 'warning' ? 'warn' : 'log'](text);
    }
  }
}

@NgModule({})
export class ToastrModule {
  static forRoot(_config?: unknown): ModuleWithProviders<ToastrModule> {
    return { ngModule: ToastrModule, providers: [ToastrService] };
  }
}

export interface NgxUiLoaderConfig {
  bgsPosition?: string;
  fgsColor?: string;
  hasProgressBar?: boolean;
}

export const POSITION = {
  bottomCenter: 'bottom-center'
};

@Injectable({ providedIn: 'root' })
export class NgxUiLoaderService {
  private readonly loadingState = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingState.asObservable();

  start(): void { this.loadingState.next(true); }
  stop(): void { this.loadingState.next(false); }
}

@Component({
    selector: 'ngx-ui-loader',
    template: '@if (loader.loading$ | async) {<div class="compat-ui-loader"><div class="compat-ui-loader-spinner"></div></div>}',
    styles: [`
    .compat-ui-loader { position: fixed; inset: 0; z-index: 9999; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,.35); pointer-events: none; }
    .compat-ui-loader-spinner { width: 44px; height: 44px; border: 4px solid rgba(242,140,6,.25); border-top-color: #f28c06; border-radius: 50%; animation: compat-spin .8s linear infinite; }
    @keyframes compat-spin { to { transform: rotate(360deg); } }
  `],
    standalone: false
})
export class NgxUiLoaderComponent {
  constructor(public loader: NgxUiLoaderService) {}
}

@NgModule({
  imports: [CommonModule],
  declarations: [NgxUiLoaderComponent],
  exports: [NgxUiLoaderComponent]
})
export class NgxUiLoaderModule {
  static forRoot(_config?: NgxUiLoaderConfig): ModuleWithProviders<NgxUiLoaderModule> {
    return { ngModule: NgxUiLoaderModule, providers: [NgxUiLoaderService] };
  }
}

@Component({
    selector: 'angular-file-uploader',
    template: `
    <div class="compat-file-uploader">
      <input type="file" [accept]="accept" (change)="upload($event)" [disabled]="uploading">
      @if (uploading) {
        <span>Subiendo...</span>
      }
    </div>
    `,
    styles: ['.compat-file-uploader { display: block; }'],
    standalone: false
})
export class AngularFileUploaderComponent {
  @Input() config: any;
  @Input() resetUpload: unknown;
  @Output() ApiResponse = new EventEmitter<HttpResponse<unknown> | { body: unknown }>();
  uploading = false;

  constructor(private http: HttpClient) {}

  get accept(): string | null {
    return this.config?.formatsAllowed || null;
  }

  resetFileUpload(): void {
    this.uploading = false;
  }

  upload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const uploadApi = this.config?.uploadAPI;
    if (!file || !uploadApi?.url) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    let headers = new HttpHeaders();
    const configuredHeaders = uploadApi.headers || {};
    Object.keys(configuredHeaders).forEach(key => {
      headers = headers.set(key, configuredHeaders[key]);
    });

    this.uploading = true;
    this.http.request(uploadApi.method || 'POST', uploadApi.url, {
      body: formData,
      headers,
      observe: 'response'
    }).subscribe({
      next: response => {
        this.uploading = false;
        this.ApiResponse.emit(response);
        input.value = '';
      },
      error: error => {
        this.uploading = false;
        this.ApiResponse.emit({ body: error.error || error });
        input.value = '';
      }
    });
  }
}

@NgModule({ declarations: [AngularFileUploaderComponent],
    exports: [AngularFileUploaderComponent], imports: [CommonModule], providers: [provideHttpClient(withInterceptorsFromDi())] })
export class AngularFileUploaderModule {}

@Component({
    selector: 'angular2-multiselect',
    template: `
    <select class="form-control" multiple [disabled]="disabled" (change)="onSelectionChange($event)">
      @for (item of data; track item) {
        <option [ngValue]="item" [selected]="isSelected(item)">{{ labelFor(item) }}</option>
      }
    </select>
    `,
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AngularMultiSelectComponent),
            multi: true
        }],
    standalone: false
})
export class AngularMultiSelectComponent implements ControlValueAccessor {
  @Input() data: any[] = [];
  @Input() settings: any;
  @Output() onSelect = new EventEmitter<any>();
  @Output() onDeSelect = new EventEmitter<any>();
  @Output() onSelectAll = new EventEmitter<any[]>();
  @Output() onDeSelectAll = new EventEmitter<any[]>();

  value: any[] = [];
  disabled = false;
  private propagateChange: (value: any[]) => void = () => {};
  private propagateTouched: () => void = () => {};

  writeValue(value: any[]): void { this.value = Array.isArray(value) ? value : []; }
  registerOnChange(fn: (value: any[]) => void): void { this.propagateChange = fn; }
  registerOnTouched(fn: () => void): void { this.propagateTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  labelFor(item: any): string { return item?.itemName ?? item?.name ?? item?.nombre ?? String(item); }
  isSelected(item: any): boolean { return this.value.some(current => this.sameItem(current, item)); }

  onSelectionChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const selected = Array.from(select.selectedOptions).map(option => this.data[option.index]).filter(Boolean);
    const previous = this.value;
    this.value = selected;
    this.propagateChange(this.value);
    this.propagateTouched();

    selected.filter(item => !previous.some(old => this.sameItem(old, item))).forEach(item => this.onSelect.emit(item));
    previous.filter(item => !selected.some(current => this.sameItem(current, item))).forEach(item => this.onDeSelect.emit(item));
    if (selected.length === this.data.length) { this.onSelectAll.emit(selected); }
    if (selected.length === 0 && previous.length > 0) { this.onDeSelectAll.emit([]); }
  }

  private sameItem(a: any, b: any): boolean {
    if (a?.id !== undefined && b?.id !== undefined) {
      return a.id === b.id;
    }
    return a === b;
  }
}

@NgModule({
  imports: [CommonModule, FormsModule],
  declarations: [AngularMultiSelectComponent],
  exports: [AngularMultiSelectComponent]
})
export class AngularMultiSelectModule {}

export interface PerfectScrollbarConfigInterface { suppressScrollX?: boolean; [key: string]: unknown; }
export const PERFECT_SCROLLBAR_CONFIG = new InjectionToken<PerfectScrollbarConfigInterface>('PERFECT_SCROLLBAR_CONFIG');

@Component({
    selector: 'perfect-scrollbar',
    template: '<div class="compat-perfect-scrollbar"><ng-content></ng-content></div>',
    styles: ['.compat-perfect-scrollbar { overflow: auto; max-width: inherit; max-height: inherit; }'],
    standalone: false
})
export class PerfectScrollbarComponent {
  @Input() config: PerfectScrollbarConfigInterface | null = null;
}

@NgModule({
  imports: [CommonModule],
  declarations: [PerfectScrollbarComponent],
  exports: [PerfectScrollbarComponent]
})
export class PerfectScrollbarModule {}

@Directive({
    selector: '[clickOutside]',
    standalone: false
})
export class ClickOutsideDirective {
  @Input() exclude = '';
  @Output() clickOutside = new EventEmitter<Event>();

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as Node | null;
    if (!target || this.elementRef.nativeElement.contains(target)) {
      return;
    }
    if (this.exclude && document.querySelector(this.exclude)?.contains(target)) {
      return;
    }
    this.clickOutside.emit(event);
  }
}

@NgModule({
  declarations: [ClickOutsideDirective],
  exports: [ClickOutsideDirective]
})
export class ClickOutsideModule {}

@NgModule({})
export class ToastyModule {
  static forRoot(): ModuleWithProviders<ToastyModule> {
    return { ngModule: ToastyModule };
  }
}

@NgModule({})
export class ChartModule {}
